import { useState, useRef } from 'react';
import type { UseInitFilterProps } from '@intelligence/pages/TopCharts/PCGames/_config/type';
import { filterByDateType } from '@intelligence/pages/TopCharts/PCGames/components/GamesDetails/Stats/AllMetrics/utils';
import {
  getDefaultMetricsOptions,
  getSourceMetricsByValue,
  getMetricOptionByValue,
  filterMetricsOptionsByDataSources,
  filterSourceByDateType,
} from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPITrends/components/MetricsSelection/config';
import {
  DATABRAIN_METRIC_MAPPING,
  buildHourlyMetricSelectionValue,
} from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPITrends/const';
import { getFilterSaveList } from '@/pages/intelligence/services/common';
import { isEmpty } from '@/pages/intelligence/utils/commonUtils';
import { getSpecialMetrictableDevice } from '@/pages/intelligence/utils/speciaFilterUtils';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import {
  feachTimeTypeToFilterTimeType,
  getDateTypeCurByActiveSegment,
  toDateTypeFilter,
  toDateTypeLy,
} from '@/pages/intelligence/utils/common';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import { useDispatch, useSelector, history } from 'umi';
import { FILTER_EXTEND_NAMESPACE } from '@/pages/intelligence/models/filterExtend';
import { DBI_TOPCHARTS } from '@/pages/intelligence/models/topcharts';
import { TopchartPageId } from '@intelligence/pages/TopCharts/_config/topChartsConst';
import {
  URL_CHART_METRIC,
  URL_SHORT_LINK_NAME,
  URL_SORT_NAME,
  URL_CHART_TYPE,
  ShortLinkPageValidCfgType,
  getOriginalShortLinkFilter,
} from '@intelligence/utils/urlPathCfg';
import { updateURLSearchParams } from '@intelligence/utils/commonUtils';
import {
  getTopPCMetricTableCustomValidUrlParams,
  topchartPCConsoleMetrictableValidCfg,
} from '@intelligence/_config/urlPageValidCfg';
import { onFilterConfirm } from '@/pages/intelligence/components/Filter/util';
import { PCConsoleSegementList } from '@/pages/intelligence/pages/TopCharts/_config/metricOptions';
import { getPCConsoleMetricValues } from '@intelligence/pages/TopCharts/PCGames/_config/utils';
import { getCascaderMetricsOptionsAndValue } from '@intelligence/pages/TopCharts/PCGames/MetricsTable/utils';
import { useDeepCompareEffect } from 'ahooks';
import useFilterUrlLinkage from './useFilterUrlLinkage';
import moment from 'moment';
import { getSinceReleaseTimeOfRangePicker } from '@intelligence/utils/topchartsUtils';

/**
 * 获取Cascader组件某个父级Option下的所有SubOptions的value集合
 * @param options Cascader组件的父级Options集合
 * @param optionValue Cascader组件的父级Option下的SubOptions的value集合
 */
const getAllSegmentsOfDataSource = (
  options?: any[] | null,
  optionValue?: string,
): string[] => {
  if (!Array.isArray(options) || !options.length) return [];
  if (!optionValue) return [];
  const option = options.find((item) => item?.value === optionValue);
  const subOptions = option?.children || [];
  return subOptions?.map((item: any) => item?.value) || [];
};

// useInitFilterHook筛选器公共组件，初始化通用hooks
const useInitFilterHook = (props: UseInitFilterProps) => {
  const {
    isBasicLoading,
    filterValue,
    setFilterValue,
    setFilterPageValue,
    filterConfirm,
    filterCfg,
    activeSegment,
    ifSetFilterExtend = true,
    defaultInputList,
    isOperateTime,
    /**
     * 筛选器地区值是否需要根据只选中 mscience 数据源的 metrics 指标时，指定 us 区域，有其他数据源指标时重置选中 global
     * 生效条件为用户未手动选中地区
     * 如用户有手动选中更改地区，不再根据 metrics mscience 数据源自动指定区域
     * 使用 updateFilterDatas 方法需传入 userManuallyChangedRegion 属性作为判断依据用户是否手动修改地区
     */
    shouldSetUSOrGlobalRegionByMetrics = false,
    hasUrlShortLink = false, // 是否支持短链接
    pageId,
    pageIds,
    dataSourcesColumns,
    setDataSourcesColumns,
    hasExtend = true, // 自定义是不需要继承
    setChartType,
    setMetric,
    // 下面四个参数是useFilterUrlLinkage这个hooks专用
    // 是否要更新url
    needUpdateUrl = false,
    // 是否需要解码url为filterValue
    needDecodeUrl = false,
    // 是否为短连接id模式
    isShortIdMode = false,
    // 是否是短链接模式
    isShortMode = false,
    // 是否为DataBrain校准数据和真数模式
    isDataBrainTrueDataMode,
    // 是否执行内部的初始化逻辑，默认值为true
    executable = true,
    filterConfig = {},
    filterValueFormatter,
    genreOptionsMap,
  } = props || {};
  // console.log('useInitFilterHook', props);

  const query = history?.location?.query || {};
  const { use_url_filter: useUrlFilter } = query || {};
  // 是否通过url设置Region筛选器value（智能搜索 情报页面AI直达功能）
  const useUrlFilterRegion = useUrlFilter && !!query?.region;
  // 是否通过url设置级联Segment筛选器value（智能搜索 情报页面AI直达功能）
  const useUrlFilterCascaderSegment = useUrlFilter && !!query?.cascader_segment;

  const dispatch = useDispatch();
  const { userInfo } = useSelector((state: any) => {
    const { userInfo } = state.home;
    return {
      userInfo,
    };
  });

  const specialParams = filterCfg?.specialParams || {};
  const { userSaveFilterValueKey, isOnlyExtendDate = false, disabledDataSources = [] } = specialParams || {};
  const [filterLoading, setFilterLoading] = useState(true);

  /** ***pcconsole-榜单所需要数据 **/
  const isDigitalRevenue = pageId === TopchartPageId.DigitalRevenue;
  const currentMetrictableValidCfg: ShortLinkPageValidCfgType = {
    ...topchartPCConsoleMetrictableValidCfg,
    [URL_SORT_NAME]: dataSourcesColumns?.map((t: any) => t.value),
  };
  const { pcConsoleCascaderMetricsOptions, pcConsoleCascaderMetricsMap } = PCConsoleSegementList;

  // const [urlCustomParams, setUrlCustomParams] = useState({});
  const [shortLinkValidQuery, setShortLinkValidQuery] = useState({
    [URL_CHART_TYPE]: currentMetrictableValidCfg?.[URL_CHART_TYPE]?.[0],
    [URL_CHART_METRIC]: currentMetrictableValidCfg?.[URL_CHART_METRIC]?.[0],
  } as any);
  const hasUrlShortLinkVal = history.location?.query?.[URL_SHORT_LINK_NAME];
  /** ***pcconsole-榜单所需要数据 **/

  // const { filterExtend } = useFilterExtend({});
  const isPageScopedExtend =
    pageId === TopchartPageId.PCConsoleMetricsTable ||
    pageId === TopchartPageId.PCConsoleDataBrainCalibration;
  const {
    isOperate: isOperateTemp,
    isOperateDetail,
    detailFilterExtend,
    filterExtend,
    setFilterExtend,
    setDetailFilterExtend,
  } = useFilterExtend(isPageScopedExtend ? { pageId } : {});

  const filterState = useSelector(
    (state: any) => state?.[FILTER_EXTEND_NAMESPACE] || {},
  );
  const topChartsState = useSelector(
    (state: any) => state?.[DBI_TOPCHARTS] || {},
  );

  const { normalTimeFilterExtend, releaseTimeFilterExtend } = filterState || {};

  // 筛选器更新，处理全局统一逻辑，更新数据，再调用
  const updateFilterDatas = (
    listKeyValue = filterValue, // 筛选器的值
    flLoading = filterLoading, // 筛选器前置loading状态
    ifGetFilterExtend = false, // 是否需要继承数据
    ifIgnoreActiveSegment = false, // 是否忽略url定向指标
    userManuallyChangedRegion = false, // 用户是否手动修改过 region 筛选项
    urlCustomParams = {}, // url参数
    userSegmentChangedRegionRef = false, // 用户是否手动修改过 产品分类 筛选项
    userSegmentCloseRef = true,
    restoreDefault = false,
    specialCascader = false,
  ) => {
    const isOperate = hasExtend && isOperateTemp;

    // console.log('listKeyValue', listKeyValue);
    // console.log('flLoading', flLoading);
    // console.log('ifGetFilterExtend', ifGetFilterExtend);
    // console.log('ifIgnoreActiveSegment', ifIgnoreActiveSegment);
    // console.log('filterExtendfilterExtend3131133131313', filterExtend);
    // console.log('isOperate', isOperate);
    // console.log('isOperateTime', isOperateTime);
    // console.log('detailFilterExtenddetailFilterExtend', detailFilterExtend);
    // console.log('isOperateDetail', isOperateDetail);
    // console.log('defaultInputList', defaultInputList);
    // console.log('topChartsStatetopChartsState', topChartsState);
    // 页面初始化数据, 因最终接口参数与指标选项，由时间粒度和指标值数组决定，所以先初始化这两个数据
    let filterValueTemp = { ...listKeyValue } as any;
    // 用户操作记忆的先更新
    // filterValueTemp = {
    //   ...filterValueTemp,
    //   userSavesDatas,
    // };
    // 是否是 Compare 页面的指标筛选器
    const isCompareMetrics = Object.prototype.hasOwnProperty.call(
      filterValueTemp,
      filterKeyMap.ALIGN_TO_RELEASE,
    );
    // console.log('filterValueTemp1111', filterValueTemp);
    // console.log(
    //   'filterValueTemp[filterKeyMap.RANGE_TIME]?.type',
    //   filterValueTemp[filterKeyMap.RANGE_TIME]?.type,
    // );
    const metricDatas =
      filterValueTemp?.[filterKeyMap.METRIC_SELECT_FOR_KPI_TREND]?.value || {};
    // console.log('metricDatasmetricDatas', metricDatas);
    const { metrics = [] } = metricDatas || {};
    // console.log('metricsmetrics', metrics);
    // 初始化时间粒度
    let dateTypeCur = filterValueTemp[filterKeyMap.RANGE_TIME]?.type;
    // const dateValue = filterValueTemp[filterKeyMap.RANGE_TIME]?.value;
    // console.log('dateTypeCur222', dateTypeCur);
    // console.log('metricsOptionsListCurmetricsOptionsListCur', metricsOptionsListCur);
    // 初始化指标metricList
    let metricListValueCur = metrics;
    // 初始化是否有定向指标
    const isTrueActiveSegment = activeSegment && activeSegment !== '';
    // 设置默认metricsvaluelist
    // let defaultMetricListValue = DEFAULTKPITRENDSSHOWRTLIST;
    let metricSourcesCur = metricDatas.sources;
    let metricTypesCur = metricDatas.types;
    let metricSelectedMetricsValueCur = metricDatas.selectedMetricsValue;
    let metricShotSelectValueCur = metricDatas.shotSelectValue;
    let selectCutValCur = metricDatas.selectCutVal;
    // console.log('selectCutValCurselectCutValCur~~~~~~~~~~', selectCutValCur);
    // console.log('isOperateDetail', isOperateDetail);
    // console.log('isOnlyExtendDate', isOnlyExtendDate);
    // console.log('filterExtend', filterExtend);

    if ((isOperateDetail || isOperate) && ifGetFilterExtend && hasExtend) {
      const extendToUse =
        (isPageScopedExtend ? filterExtend : detailFilterExtend) || filterExtend;

      let rangeTimeTemp = extendToUse?.rangeTime || filterValueTemp?.rangeTime;
      // 若只继承日粒度
      if (
        isOnlyExtendDate &&
        toDateTypeFilter[extendToUse?.rangeTime?.type] !== 'date'
      ) {
        rangeTimeTemp = filterValueTemp?.rangeTime;
      }
      filterValueTemp = {
        ...filterValueTemp,
        ...extendToUse,
        rangeTime: rangeTimeTemp,
      };

      // 获取定向指标metricList
      metricListValueCur =
        extendToUse?.metric_selection_for_trends?.value?.metrics;
      metricShotSelectValueCur = 'all';
      metricSourcesCur = ['all'];
      metricTypesCur = 'all';
      metricSelectedMetricsValueCur = 'metrics';
      selectCutValCur = 'all';
      dateTypeCur = filterValueTemp[filterKeyMap.RANGE_TIME]?.type;
    }

    // console.log('init-isTrueActiveSegment', isTrueActiveSegment);
    // console.log('init-activeSegment', activeSegment);
    // console.log('init-ifIgnoreActiveSegment', ifIgnoreActiveSegment);
    // console.log('init-topChartsState?.topchartJumpParam', topChartsState?.topchartJumpParam);

    if (filterValueTemp?.[filterKeyMap.METRICTABLE_DEVICE]) {
      filterValueTemp = {
        ...filterValueTemp,
        metrictable_device: {
          value: getSpecialMetrictableDevice(
            filterValueTemp?.metrictable_device?.value,
            metricListValueCur,
            isTrueActiveSegment,
          ),
        },
      };
    }
    if (pageId === 'segmentDefauleValue') {
      if (!userSegmentChangedRegionRef || restoreDefault) {
        const metricListValueTemp =
          filterValueTemp[filterKeyMap.METRIC_SELECT_FOR_KPI_TREND]?.value;
        // const metricsItemList = metricListValueTemp?.map(
        //   (item: any) => pcConsoleCascaderMetricsMap?.[item],
        // );
        if (metricListValueTemp?.metrics?.length > 0 && !isUrlParamsUpdate && !useUrlFilterCascaderSegment) {
          filterValueTemp = {
            ...filterValueTemp,
            cascader: {
              value: {
                mscience:
                  metricListValueTemp.metrics.indexOf('units') > -1
                    ? ['Full Game']
                    : metricListValueTemp.metrics.indexOf('gsd_digitalUnits') >
                      -1
                    ? []
                    : [
                        'Full Game',
                        'MTX',
                        'DLC: All Other',
                        'DLC: Expansion',
                        'DLC: Content Pre-Purchase Pass',
                        'DLC: Unlockable Content Pass',
                        'Subscription',
                      ],
                gsd:
                  metricListValueTemp.metrics.indexOf('gsd_digitalUnits') > -1
                    ? ['Full Game']
                    : metricListValueTemp.metrics.indexOf('units') > -1
                    ? []
                    : ['Full Game', 'In-Game Currency'],
              },
            },
          };
        }
      }
      if (userSegmentCloseRef === false && userSegmentChangedRegionRef) {
        return;
      }
      if (userSegmentCloseRef === false && specialCascader) {
        return;
      }
    }

if (isDataBrainTrueDataMode === 'true' && !ifIgnoreActiveSegment) {
      console.log('执行DataBrain跳转逻辑，activeSegment:', activeSegment);
      const cascaderInput = defaultInputList?.find(
        (item: any) => item?.key === filterKeyMap.CASCADER,
      );

      // 根据 activeSegment 映射到对应的三个预选指标
      // activeSegment 直接是指标标识符，不需要标准化
      // 每个DataBrain指标都对应三个预选指标
      const dataBrainMetricMapping = DATABRAIN_METRIC_MAPPING;

      // 获取对应的三个预选指标，如果没有匹配则使用默认的Revenue指标组
      const targetMetrics = dataBrainMetricMapping[activeSegment] || [
        activeSegment,
      ];
      console.log(
        'activeSegment:',
        activeSegment,
        '→ targetMetrics:',
        targetMetrics,
      );

      // 设置对应的三个预选指标
      metricListValueCur = Array.isArray(targetMetrics)
        ? targetMetrics
        : [activeSegment];

      // 获取游戏上线日期作为开始日期
      const releaseTime = topChartsState?.baseReleaseTime || '';
      const startDate = getSinceReleaseTimeOfRangePicker(releaseTime);
      const endDate = moment().format('YYYY-MM-DD');

      // 设置日期筛选器为 Day 粒度，从游戏上线至今
      filterValueTemp = {
        ...filterValueTemp,
        rangeTime: {
          type: 'date', // Day 粒度
          value: [startDate, endDate], // 使用游戏上线日期作为开始，今天作为结束
        },
      };

      // 设置 Region 筛选器为 ALL (global)
      // 当从 AI 导航跳转过来时，跳过覆盖 region 的逻辑
      if (!useUrlFilterRegion) {
        filterValueTemp = {
          ...filterValueTemp,
          region: {
            ...filterValueTemp?.region,
            markets: ['global'], // ALL regions
            regions: ['global'],
            regionType: 'region',
          },
        };
      }

      // 设置 Segment For Sales 为 ALL
      filterValueTemp = {
        ...filterValueTemp,
        digital_segment_multiple: {
          ...filterValueTemp?.digital_segment_multiple,
          value: ['All'], // ALL segments
        },
      };

      // 设置级联筛选器 (Segment) 为 ALL
      if (cascaderInput) {
        filterValueTemp = {
          ...filterValueTemp,
          cascader: {
            ...filterValueTemp?.cascader,
            value: {
              ...filterValueTemp?.cascader?.value,
              mscience: getAllSegmentsOfDataSource(
                cascaderInput?.options?.children,
                'mscience',
              ), // ALL segments
            },
          },
        };
      }

      metricShotSelectValueCur = 'all';
      metricSourcesCur = ['all'];
      metricTypesCur = 'all';
      metricSelectedMetricsValueCur = 'metrics';
      selectCutValCur = 'all';
      dateTypeCur = filterValueTemp[filterKeyMap.RANGE_TIME]?.type;

      // topchartJumpParam，只临时用一次
      dispatch({
        type: `${DBI_TOPCHARTS}/setTopchartJumpParam`,
        payload: {},
      });
    }
    // 如果是跳转定向指标（非 DataBrain 跳转）
    else if (isTrueActiveSegment && !ifIgnoreActiveSegment) {
      // 如果定向指标是gsd_digitalUnits，则时间粒度为weekly
      const isGsd = activeSegment === 'gsd_digitalUnits';
      const isUnitsJumpFromKPIOverview = activeSegment === 'salesUnits';
      const overviewSinceReleaseJumpTabs = [
        'd1afterreleaserevenue',
        'd1afterreleasesales',
        'w1afterreleaserevenue',
        'w1afterreleasesales',
        'm1afterreleaserevenue',
        'm1afterreleasesales',
      ];
      const overviewSinceReleaseJumpDateTypeMap: Record<string, string> = {
        d1afterreleaserevenue: 'daily',
        d1afterreleasesales: 'daily',
        w1afterreleaserevenue: 'weekly',
        w1afterreleasesales: 'weekly',
        m1afterreleaserevenue: 'monthly',
        m1afterreleasesales: 'monthly',
      };
      const overviewJumpActiveTab = `${topChartsState?.topchartJumpParam?.activeTab || ''}`.toLocaleLowerCase();
      const isOverviewSinceReleaseJump =
        topChartsState?.topchartJumpParam?.from === 'overview' &&
        overviewSinceReleaseJumpTabs.includes(overviewJumpActiveTab);
      const overviewSinceReleaseJumpDateType =
        topChartsState?.topchartJumpParam?.from === 'overview'
          ? overviewSinceReleaseJumpDateTypeMap[overviewJumpActiveTab]
          : '';
      let activeDateType = dateTypeCur;
      const dateTypeByActiveSegment =
        getDateTypeCurByActiveSegment(activeSegment);
      if (overviewSinceReleaseJumpDateType) {
        activeDateType = overviewSinceReleaseJumpDateType;
      } else if (topChartsState?.topchartJumpParam?.timeType) {
        activeDateType = topChartsState?.topchartJumpParam?.timeType;
      } else if (dateTypeByActiveSegment) {
        activeDateType = dateTypeByActiveSegment;
      }

      filterValueTemp = {
        ...filterValueTemp,
        rangeTime: {
          type: feachTimeTypeToFilterTimeType(activeDateType),
          value: isOverviewSinceReleaseJump
            ? [
                getSinceReleaseTimeOfRangePicker(topChartsState?.baseReleaseTime || ''),
                moment().format('YYYY-MM-DD'),
              ]
            : ['', ''],
        },
      };
      const cascaderInput = defaultInputList?.find(
        (item: any) => item?.key === filterKeyMap.CASCADER,
      );
      // console.log('isUnitsJumpFromKPIOverview', isUnitsJumpFromKPIOverview);
      // 卡片定向跳转特定segment值
      let segmentValueTemp = 'All';
      if (isGsd) {
        segmentValueTemp = 'Full Game';
      } else if (isUnitsJumpFromKPIOverview) {
        if (topChartsState?.topchartJumpParam?.targetSegment) {
          segmentValueTemp = topChartsState?.topchartJumpParam?.targetSegment;
        }
      }
      filterValueTemp = {
        ...filterValueTemp,
        digital_segment_multiple: {
          ...filterValueTemp?.digital_segment_multiple,
          value: [segmentValueTemp],
        },
        cascader: {
          ...filterValueTemp?.cascader,
          value: {
            ...filterValueTemp?.cascader?.value,
            mscience:
              segmentValueTemp !== 'All'
                ? [segmentValueTemp]
                : getAllSegmentsOfDataSource(
                    cascaderInput?.options?.children,
                    'mscience',
                  ),
          },
        },
      };
      // console.log('filterValueTemp', filterValueTemp);

      // 定向指标，国家都重置为global
      // 当从 AI 导航跳转过来时，跳过覆盖 region 的逻辑
      if (!useUrlFilterRegion) {
        filterValueTemp = {
          ...filterValueTemp,
          region: {
            ...filterValueTemp?.region,
            markets: ['global'],
            regions: ['global'],
          },
        };
      }
      // 获取定向指标metricList
      if (typeof activeSegment === 'string') {
        // metricListValueCur = ['gsd_digitalUnits'];
        if (isUnitsJumpFromKPIOverview) {
          metricListValueCur = ['units'];
        } else {
          metricListValueCur = [activeSegment];
        }
      }
      // console.log('dateTypeCurdateTypeCur~~~~~~~~~~', dateTypeCur);
      metricShotSelectValueCur = 'all';
      metricSourcesCur = ['all'];
      metricTypesCur = 'all';
      metricSelectedMetricsValueCur = 'metrics';
      selectCutValCur = 'all';
      dateTypeCur = filterValueTemp[filterKeyMap.RANGE_TIME]?.type;

      // topchartJumpParam，只临时用一次
      dispatch({
        type: `${DBI_TOPCHARTS}/setTopchartJumpParam`,
        payload: {},
      });
    }
    // KPI Overview Steam Hourly CCU：无 URL segment，仅靠 jump param 切 Hour segment；
    // 指标由下方 hourly 分支 buildHourlyMetricSelectionValue() 统一覆盖
    else if (
      !ifIgnoreActiveSegment &&
      !isTrueActiveSegment &&
      topChartsState?.topchartJumpParam?.from === 'overview' &&
      topChartsState?.topchartJumpParam?.timeType === 'hour' &&
      topChartsState?.topchartJumpParam?.activeTab
    ) {
      filterValueTemp = {
        ...filterValueTemp,
        rangeTime: {
          type: feachTimeTypeToFilterTimeType('hour'),
          value: ['', ''],
        },
      };
      dateTypeCur = filterValueTemp[filterKeyMap.RANGE_TIME]?.type;

      dispatch({
        type: `${DBI_TOPCHARTS}/setTopchartJumpParam`,
        payload: {},
      });
    }

    // 特殊页面pageId，特殊处理逻辑
    if (pageId === TopchartPageId.PCConsoleMetricsTable) {
      // 在筛选器这里拿到缓存的值，并渲染
      const metricListValueTemp =
        filterValueTemp[filterKeyMap.PCCONSOLE_TOPCHARTS_METRICS]?.value;
      const metricsItemList = metricListValueTemp?.map(
        (item: any) => pcConsoleCascaderMetricsMap?.[item],
      );
      const metricObjValues = getPCConsoleMetricValues(metricsItemList);

      // 用于榜单metricTable的指标筛选器继承到详情页
      const metricListValueParams: string[] = [];
      const sourceMetricsValue = metricObjValues?.source_metrics || {};
      // eslint-disable-next-line no-restricted-syntax
      for (const sourceKey in sourceMetricsValue) {
        metricListValueParams.push(...sourceMetricsValue[sourceKey]);
      }
      filterValueTemp = {
        ...filterValueTemp,
        // DigitalRevenue 页面地区筛选器默认 us 避免被继承影响
        ...(isDigitalRevenue
          ? {
              [filterKeyMap.REGION]: {
                regions: ['us'],
                markets: ['us'],
                type: 'region',
              },
            }
          : {}),
        metric_selection_for_trends: {
          value: {
            type: 'monthly',
            types: 'all',
            metrics: metricListValueParams,
            sources: ['all'],
            source_metrics: getSourceMetricsByValue(metricListValueParams),
            metricsOptions: getMetricOptionByValue(metricListValueParams),
            selectedMetricsValue: 'metrics',
            shotSelectValue: 'all',
            selectCutVal: 'hot',
          },
        },
      };
    }

    // console.log('filterValueTempfilterValueTempe2222', filterValueTemp);

    // console.log('normalTimeFilterExtendnormalTimeFilterExtend~~~~~~~~~~', normalTimeFilterExtend);
    // 如果有rangeReleaseTime筛选器，保存两种时间
    if (isCompareMetrics) {
      const typeValueNormal = filterValueTemp?.rangeTime?.type;
      const typeValueRelease = filterValueTemp?.rangeReleaseTime?.type;
      dispatch({
        type: `${FILTER_EXTEND_NAMESPACE}/setNormalTimeFilterExtend`,
        payload: {
          ...normalTimeFilterExtend,
          [typeValueNormal]: filterValueTemp?.rangeTime?.value,
        },
      });
      dispatch({
        type: `${FILTER_EXTEND_NAMESPACE}/setReleaseTimeFilterExtend`,
        payload: {
          ...releaseTimeFilterExtend,
          [typeValueRelease]: filterValueTemp?.rangeReleaseTime?.value,
        },
      });
    }

    // 特殊逻辑
    // combine-storerank-detail-market count-数据源榜单epic为禁选，初始化为steam
    if (
      filterValueTemp?.storerank_segement?.value === 'market' &&
      filterValueTemp?.composed_select?.value?.parent === 'epic'
    ) {
      filterValueTemp = {
        ...filterValueTemp,
        composed_select: {
          ...filterValueTemp?.composed_select,
          value: {
            child: 'steam top sellers',
            parent: 'steam',
          },
        },
      };
    }

    // 特殊逻辑
    // combine-storerank-detail 若没操作过,epic使用前一天的30天，暂时不上
    // if (
    //   !isOperateTime &&
    //   filterValueTemp?.composed_select?.value?.parent === 'epic' &&
    //   filterValueTemp?.storerank_segement?.value === 'rank'
    // ) {
    //   filterValueTemp = {
    //     ...filterValueTemp,
    //     rangeTime: {
    //       ...filterValueTemp?.rangeTime,
    //       value: [
    //         moment().subtract(30, 'day').format('YYYY-MM-DD'),
    //         // moment().subtract(1, 'months').subtract(1, 'day').format('YYYY-MM-DD'),
    //         moment().subtract(1, 'day').format('YYYY-MM-DD'),
    //       ],
    //     },
    //   };
    // }

    // 当从 AI 导航跳转过来时，跳过根据 metrics 自动设置 region 的逻辑
    // 避免覆盖 URL 参数中的 region 值
    if (shouldSetUSOrGlobalRegionByMetrics && !useUrlFilterRegion) {
      // 只选中 mscience 指标，地区默认使用 us
      // const metricOptions = listKeyValue?.metric_selection_for_trends?.value?.metricsOptions;
      const metricOptions =
        filterValueTemp?.metric_selection_for_trends?.value?.metricsOptions;

      const isOnlyMscienceMetric = metricOptions?.every(
        (item: any) => item?.dataSource === 'mscience',
      );

      // 只有 mscience 数据源的指标且用户未改变地区
      if (isOnlyMscienceMetric && !userManuallyChangedRegion) {
        // 定向指标，国家都重置为global
        filterValueTemp = {
          ...filterValueTemp,
          region: {
            ...filterValueTemp?.region,
            markets: ['us'],
            regions: ['us'],
          },
        };
      }

      if (!isOnlyMscienceMetric && !userManuallyChangedRegion && !isOperate) {
        // 定向指标，国家都重置为global
        filterValueTemp = {
          ...filterValueTemp,
          region: {
            ...filterValueTemp?.region,
            markets: ['global'],
            regions: ['global'],
          },
        };
      }
    }

    // 特殊指标，特殊处理
    // 1、如果指标都为Other Vendor，则Platform不勾选Nintendo，并禁用
    // 如果有指标筛选器，则更新其指标内容
    if (filterValueTemp?.[filterKeyMap.METRIC_SELECT_FOR_KPI_TREND]) {
    // Hourly 模式：时间粒度为小时时，数据源只支持 Steam，指标只支持 CCU
      // 因此强制覆盖用户的指标选择，使用默认的 CCU 指标配置
      if (toDateTypeLy[dateTypeCur] === 'hourly') {
        const hourlyMetricValue = buildHourlyMetricSelectionValue();
        filterValueTemp = {
          ...filterValueTemp,
          metric_selection_for_trends: {
            value: {
              ...hourlyMetricValue,
              metricsLoading: flLoading,
            },
          },
        };
      } else {
        // 非 Hourly 模式：根据当前时间粒度动态过滤可用指标
        // 用户可以选择多个指标，支持多数据源
      // 最终初始化获取的指标数据，由指标和时间粒度决定
      // 获取指标操作对象,过滤全量指标匹配
      let metricsOptionsListCurTemp =
        getMetricOptionByValue(metricListValueCur);
      metricsOptionsListCurTemp = filterMetricsOptionsByDataSources(metricsOptionsListCurTemp, disabledDataSources);
      // 按照配置项的原始顺序获取当前配置项顺序(表格列配置使用)
      let metricsOptionsListByConfigOrderTemp = getMetricOptionByValue(
        metricListValueCur,
        true,
      );
      metricsOptionsListByConfigOrderTemp = filterMetricsOptionsByDataSources(metricsOptionsListByConfigOrderTemp, disabledDataSources);

      metricListValueCur = metricsOptionsListCurTemp?.map((item: any) => item?.value);

      // console.log('metricsOptionsListCurTempmetricsOptionsListCurTemp', metricsOptionsListCurTemp);
      let metricsOptionsListCur = metricsOptionsListCurTemp;

      let metricsOptionsListByConfigOrderCur = metricsOptionsListByConfigOrderTemp;

      // console.log('dateTypeCurdateTypeCur', dateTypeCur);
      // console.log('metricListValueCurmetricListValueCur', metricListValueCur);
      // console.log('metricsOptionsListCurmetricsOptionsListCur', metricsOptionsListCur);
      // 指标筛选器,通过当前时间粒度，过滤出list 和 value
      const {
        filterOption: metricsOptionsListCurFilter,
        filterList: metricListValueCurFilter,
      } = filterByDateType(
        metricsOptionsListCur,
        dateTypeCur,
        isCompareMetrics,
      );
      // 通过粒度筛选出按照配置顺序的配置列
      const { filterOption: metricsOptionsListByConfigOrderCurFilter } =
        filterByDateType(
          metricsOptionsListByConfigOrderCur,
          dateTypeCur,
          isCompareMetrics,
        );

      // 获取指标操作对象,过滤时间粒度指标匹配
      // 如果未匹配上，需要给默认值，则其他配置都恢复默认
      if (isEmpty(metricListValueCurFilter)) {
        const defaultMetricsOptions = filterMetricsOptionsByDataSources(
          getDefaultMetricsOptions(isCompareMetrics),
          disabledDataSources,
        );

        metricsOptionsListCur = defaultMetricsOptions;
        metricsOptionsListByConfigOrderCur = defaultMetricsOptions;
        metricListValueCur = defaultMetricsOptions?.map((item: any) => item?.value);
        metricSourcesCur = ['all'];
        metricTypesCur = 'all';
        metricSelectedMetricsValueCur = 'metrics';
        metricShotSelectValueCur = 'all';
        selectCutValCur = 'hot';
      } else {
        metricsOptionsListCur = metricsOptionsListCurFilter;
        metricsOptionsListByConfigOrderCur =
          metricsOptionsListByConfigOrderCurFilter;
        metricListValueCur = metricListValueCurFilter;
      }

      // console.log('metricListValueCurFilter', metricListValueCurFilter);
      // console.log('isComapreMetircsisComapreMetircs', isCompareMetrics);

      // 获取最后的sourceMetrics
      const sourceMetricsObjCur = getSourceMetricsByValue(metricListValueCur);

      filterValueTemp = {
        ...filterValueTemp,
        metric_selection_for_trends: {
          value: {
            sources: filterSourceByDateType(metricSourcesCur, dateTypeCur),
            types: metricTypesCur,
            metrics: metricListValueCur,
            source_metrics: sourceMetricsObjCur,
            metricsOptions: metricsOptionsListCur,
            // 表格列使用配置，用配置项的顺序，避免和选项数据变动同步
            metricsOptionsByConfigOrder: metricsOptionsListByConfigOrderCur,
            type: dateTypeCur,
            selectedMetricsValue: metricSelectedMetricsValueCur,
            shotSelectValue: metricShotSelectValueCur,
            selectCutVal: selectCutValCur,
            metricsLoading: flLoading,
          },
        },
      };
      try {
        if (pageIds === 'segmentDefauleValues') {
          const opinions =
            filterValueTemp.metric_selection_for_trends?.value?.source_metrics
              ?.opinions;
          const metricsOptions =
            filterValueTemp.metric_selection_for_trends?.value
              ?.metricsOptions || [];
          const metricsOptionsByConfigOrder =
            filterValueTemp.metric_selection_for_trends?.value
              ?.metricsOptionsByConfigOrder || [];
          const metrics =
            filterValueTemp.metric_selection_for_trends?.value?.metrics || [];

          if (opinions && Array.isArray(opinions)) {
            opinions.forEach((key: any) => {
              filterValueTemp.metric_selection_for_trends.value.metricsOptions =
                metricsOptions.filter((item: any) => item?.value !== key);
              filterValueTemp.metric_selection_for_trends.value.metricsOptionsByConfigOrder =
                metricsOptionsByConfigOrder.filter(
                  (item: any) => item?.value !== key,
                );
              filterValueTemp.metric_selection_for_trends.value.metrics =
                metrics.filter((item: any) => item !== key);
            });

            delete filterValueTemp.metric_selection_for_trends?.value
              ?.source_metrics?.opinions;
          }
        }
      } catch (error) {}
    }
    }

    // 如果需要设置继承值，要继承的值，最后处理
    if (ifSetFilterExtend || isOperate || hasUrlShortLinkVal) {
      setFilterExtend(filterValueTemp);
      setDetailFilterExtend?.(filterValueTemp);
      dispatch({
        type: `${FILTER_EXTEND_NAMESPACE}/saveIsOperate`,
        payload: true,
      });
    }
    const { apiValue } = onFilterConfirm(filterValueTemp);

    // 更新特殊处理后的值
    setFilterValue?.(filterValueTemp);
    // 调用页面confirm事件
    filterConfirm?.(filterValueTemp, apiValue, urlCustomParams);
    setFilterPageValue?.(filterValueTemp);
  };

  // 获取用户操作记录筛选器数据
  const getUserSaveFilterDatas = async (userSaveFilterValueKey: string) => {
    setFilterLoading(true);
    try {
      const paramsUserSave = {
        type: userSaveFilterValueKey,
      };
      const res = await getFilterSaveList(paramsUserSave);
      const resData = res?.data || [];
      const resDataObj = resData?.find(
        (t: any) => t?.name === userSaveFilterValueKey,
      );
      const userSaveResult = resDataObj?.data
        ? JSON.parse(resDataObj?.data)
        : {};
      let filterValueCur = {
        ...filterValue,
        ...userSaveResult,
      } as any;
      // userSaveFilterValueKey 为 usersave-compare-pcconsole-games，不保存时间值

      if (!userSaveResult?.rangeTime?.type) {
        filterValueCur = {
          ...filterValueCur,
          rangeTime: {
            type: 'month',
            value: ['', ''],
          },
        };
      }
      updateFilterDatas(filterValueCur, false, true);
    } catch (error) {
      console.error(error);
    } finally {
      setFilterLoading(false);
    }
  };

  // 获取url参数筛选器数据
  const getShortLinkParams = async () => {
    // 在url上取token，有则请求接口，判断异常，返回函数值和url上的其他配置参数
    if (pageId === TopchartPageId.PCConsoleMetricsTable) {
      setFilterLoading(true);
      // eslint-disable-next-line prefer-const
      const {
        filter: urlFilter,
        metricFilterValue: metricFilterValueTemp,
        tableColumns: tableColumnsTemp,
      } = await getOriginalShortLinkFilter();
      let metricFilterValueCur = metricFilterValueTemp;
      let tableColumnsCur = tableColumnsTemp;
      /**
       * vginsights weighted_price 指标已移除，链接携带对应指标同步移除
       * （后续时间久了，不存在携带相关指标链接，相关代码可以移除）
       */
      if (
        metricFilterValueCur?.length === 1 &&
        metricFilterValueCur.includes('steamData###weighted_price')
      ) {
        // 只选中一项已移除指标，将链接上 custom_metric 标识移除
        updateURLSearchParams({}, ['custom_metrics']);
      }
      metricFilterValueCur = metricFilterValueCur?.filter(
        (item: any) => item !== 'steamData###weighted_price',
      );
      tableColumnsCur = tableColumnsCur?.filter(
        (item: any) =>
          !(
            item?.dataSource === 'vginsights' &&
            item?.value === 'weighted_price'
          ),
      );
      let filterValueCur = filterValue;
      // 先继承url筛选器value
      filterValueCur = {
        ...filterValueCur,
        ...urlFilter,
      } as any;

      if (Array.isArray(metricFilterValueCur) && metricFilterValueCur?.length > 0) {
        const rangeTime = filterValueCur?.[filterKeyMap.RANGE_TIME] || {};
        const rangeValue = rangeTime?.value || [];
        const regionValue = filterValueCur?.[filterKeyMap.REGION] || {};
        const shortLinkMetricParams = {
          date_type: toDateTypeLy[rangeTime?.type] || rangeTime?.type,
          regions:
            Array.isArray(regionValue?.regions) && regionValue.regions.length > 0
              ? regionValue.regions
              : regionValue?.markets || [],
          device: filterValueCur?.[filterKeyMap.METRICTABLE_DEVICE]?.value || [],
          start_date: rangeValue?.[0],
          end_date: rangeValue?.[1],
        };
        const { value: validMetricValues } = getCascaderMetricsOptionsAndValue(
          TopchartPageId.PCConsoleMetricsTable,
          pcConsoleCascaderMetricsOptions,
          metricFilterValueCur,
          shortLinkMetricParams,
          true,
        );
        if (Array.isArray(validMetricValues) && validMetricValues.length > 0) {
          metricFilterValueCur = validMetricValues;
          const validTableColumns = validMetricValues
            .map((item: any) => pcConsoleCascaderMetricsMap?.[item])
            .filter(Boolean);
          if (validTableColumns.length > 0) {
            tableColumnsCur = validTableColumns;
          }
        }
      }

      // 单独继承url指标参数
      if (
        Array.isArray(metricFilterValueCur) &&
        metricFilterValueCur?.length > 0
      ) {
        filterValueCur = {
          ...filterValueCur,
          [filterKeyMap.PCCONSOLE_TOPCHARTS_METRICS]: {
            value: metricFilterValueCur,
          },
        } as any;
      }
      let currentMetrictableValidCfg = {} as any;
      let urlCustomParamsTemp = {} as any;

      if (urlFilter) {
        let updateTableColumns = dataSourcesColumns;
        if (Array.isArray(tableColumnsCur) && tableColumnsCur?.length) {
          currentMetrictableValidCfg = {
            ...topchartPCConsoleMetrictableValidCfg,
            [URL_SORT_NAME]: tableColumnsCur?.map(
              (t: { value: string }) => t.value,
            ),
          };
          updateTableColumns = tableColumnsCur;
          setDataSourcesColumns?.(tableColumnsCur);
        }

        // 有的筛选器的token时才用后面的参数，没有则不用
        const { params, validQuery } = getTopPCMetricTableCustomValidUrlParams(
          history,
          currentMetrictableValidCfg,
          updateTableColumns,
        );
        urlCustomParamsTemp = {
          params,
          isNeedLineChartDataValue: validQuery?.chart_type === 'line',
        };
        setChartType?.(validQuery?.chart_type);
        setMetric?.(validQuery?.chart_metric);
        dispatch({
          type: `${DBI_TOPCHARTS}/setMetricValue`,
          payload: validQuery?.chart_metric,
        });
        setShortLinkValidQuery({ ...shortLinkValidQuery, ...validQuery });
      }
      updateFilterDatas(
        filterValueCur,
        false,
        hasExtend,
        false,
        false,
        urlCustomParamsTemp,
      );
      setFilterLoading(false);
    }
  };

  const {
    encodeStr,
    encodeOirginalStr,
    shortId,
    decodeObj,
    isUrlParamsUpdate = false,
    updateUrl,
  } = useFilterUrlLinkage({
    filterData: filterValue,
    config: filterConfig || {},
    isShortIdMode: !!isShortIdMode,
    isShortMode: !!isShortMode,
    needUpdateUrl: !!needUpdateUrl,
    needDecodeUrl: !!needDecodeUrl,
  });

  // Track if we've processed fromGameInfoTag URL params
  const hasProcessedGameInfoTagRef = useRef<boolean>(false);
  // Track if we've processed use_url_filter URL params
  const hasProcessedUrlFilterRef = useRef<boolean>(false);

  // 使用新的公共筛选器组件hooks，初始化方法
  useDeepCompareEffect(() => {
    if (executable === false) {
      return;
    }

    if (isBasicLoading) return;

    if (!userInfo?.userid) {
      // 拿缓存值需要拿到用户id
      return;
    }

    // Handle navigation from GameInfoTagCard via URL query params (game engine tag click)
    // This handles the case when user clicks a tag (steam_tag, game_mode, game_engine) in game detail page
    const query = history.location.query || {};
    if (query?.fromGameInfoTag === 'true' && !hasProcessedGameInfoTagRef.current) {
      hasProcessedGameInfoTagRef.current = true;

      const tagType = query.tagType as string;
      const tagValue = query.tagValue as string;

      console.log('[useInitFilterHook] Detected navigation from GameInfoTagCard:', { tagType, tagValue });

      // Reconstruct filter values with the clicked tag (same logic as mobile)
      const resetTypes = [
        filterKeyMap.STEAM_TAGS,
        filterKeyMap.GAME_MODE,
        filterKeyMap.GAME_ENGINE,
      ].filter((key) => key !== tagType);

      const resetFilters = resetTypes.reduce((acc, key) => {
        acc[key] = { value: [] };
        return acc;
      }, {} as { [key: string]: { value: string[] } });

      const newFilterValue = {
        ...filterValue,
        ...resetFilters,
        [tagType]: {
          value: [tagValue],
          ...(tagType === filterKeyMap.GAME_ENGINE ? { logic: 'OR' } : {}),
        },
      };

      // Update Redux state for filter inheritance
      setFilterExtend(newFilterValue);
      dispatch({
        type: `${FILTER_EXTEND_NAMESPACE}/saveIsOperate`,
        payload: true,
      });

      // Clear URL params to prevent re-triggering on refresh
      history.replace(history.location.pathname);

      // Trigger data fetch with the new filter values
      updateFilterDatas(newFilterValue, false, false);
      return;
    }

    // Handle use_url_filter URL parameters (from CasualGenreProfile or similar pages)
    // When URL contains use_url_filter=1, use specialized URL param parsing logic
    if (useUrlFilter && !hasProcessedUrlFilterRef.current) {
      hasProcessedUrlFilterRef.current = true;

      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const { parseUrlFilterParams } = require('@intelligence/v3/utils/urlFilterParamsAnalyser');

      const { filterParams: urlFilterParams, customParams: urlCustomParamsParsed } =
        parseUrlFilterParams(query, {
          genreOptionsMap,
        });

      // Merge URL filter params into filterValue
      const newFilterValue = {
        ...filterValue,
        ...urlFilterParams,
      };

      // Update Redux state for filter inheritance
      setFilterExtend(newFilterValue);
      dispatch({
        type: `${FILTER_EXTEND_NAMESPACE}/saveIsOperate`,
        payload: true,
      });

      // Clear URL params to prevent re-triggering on refresh
      history.replace(history.location.pathname);

      // Trigger data fetch with the new filter values and custom params
      updateFilterDatas(newFilterValue, false, false, false, false, {
        params: urlCustomParamsParsed,
        isNeedLineChartDataValue: false,
      });
      return;
    }

    // Skip the normal URL decode logic if we've already processed use_url_filter
    // This prevents duplicate API calls
    if (hasProcessedUrlFilterRef.current) {
      return;
    }

    const overviewSinceReleaseJumpActiveTabs = [
      'd1afterreleaserevenue',
      'd1afterreleasesales',
      'w1afterreleaserevenue',
      'w1afterreleasesales',
      'm1afterreleaserevenue',
      'm1afterreleasesales',
    ];
    const overviewJumpActiveTab = `${topChartsState?.topchartJumpParam?.activeTab || ''}`.toLocaleLowerCase();
    const shouldWaitOverviewSinceReleaseUrlDecode =
      needDecodeUrl &&
      !!history.location.search &&
      !isUrlParamsUpdate &&
      topChartsState?.topchartJumpParam?.from === 'overview' &&
      overviewSinceReleaseJumpActiveTabs.includes(overviewJumpActiveTab);
    if (shouldWaitOverviewSinceReleaseUrlDecode) {
      return;
    }

    const shouldWaitUrlDecode =
      needDecodeUrl &&
      !!history.location.search &&
      !isUrlParamsUpdate;
    if (shouldWaitUrlDecode) {
      return;
    }

    if (needDecodeUrl && isUrlParamsUpdate && !isEmpty(decodeObj)) {
      const newValue = filterValueFormatter
        ? filterValueFormatter(decodeObj, filterValue)
        : decodeObj;
      updateFilterDatas(newValue, false, false);
    } else if (
      hasUrlShortLink &&
      pageId === TopchartPageId.PCConsoleMetricsTable
    ) {
      // 若页面支持短链接，且为有处理逻辑的pageId时
      getShortLinkParams();
    } else if (userSaveFilterValueKey && userSaveFilterValueKey !== '') {
      // 是否请求完外basic接口，若有用户记忆参数，则先调用记忆内容
      getUserSaveFilterDatas(userSaveFilterValueKey);
    } else {
      // 若没有，则调用第一次更新
      updateFilterDatas(filterValue, false, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBasicLoading, executable, userInfo, isUrlParamsUpdate]);

  return {
    updateFilterDatas,
    shortLinkValidQuery,
    setShortLinkValidQuery,
    decodeUrlToFilterValue: needDecodeUrl ? decodeObj || {} : {},
    updateUrl: needUpdateUrl ? updateUrl : undefined,
  };
};

export default useInitFilterHook;
